import {
  Box,
  SimpleGrid,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  HStack,
  Badge,
  VStack,
  Flex,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Card,
  Checkbox,
  Divider,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Heading,
} from "@chakra-ui/react";
import {
  MdVisibility,
  MdEdit,
  MdDelete,
  MdSearch,
  MdAdd,
  MdMoreVert,
  MdGridView,
  MdList,
  MdChevronLeft,
  MdChevronRight,
  MdWarning,
  MdCheckCircle,
  MdWarehouse,
  MdLocalShipping,
  MdArrowForward,
  MdArrowBack,
  MdInventory,
  MdAddCircle,
  MdRemoveCircle,
} from "react-icons/md";
import MaterialStatCard from "components/card/MaterialStatCard";
import StockTable from "./StockTable";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useNavbarAction } from "contexts/NavbarActionContext";
import LoadingSpinner from "components/LoadingSpinner";
import {
  getMateriels,
  getVehicules,
  checkToken,
  getUser,
  getAllStock,
  createStock,
  updateStock,
  deleteStock,
  assignStockToAmbulance,
  returnMaterielToStock,
  incrementMateriel,
  decrementMateriel,
  deleteMateriel,
} from "../../../api/api";
import { usePermissions } from "hooks/usePermissions";

// Type color helper
const getTypeColor = (type) => {
  const colors = {
    'Dispositif Médical': 'red',
    'Pharmacie': 'green',
    'Autre': 'gray'
  };
  return colors[type] || 'gray';
};

export default function GestionStock() {
  const navigate = useNavigate();
  const { navbarHeight, showStats } = useNavbarAction();
  const { hasPermission } = usePermissions();
  const toast = useToast();
  const cancelRef = useRef();

  // Data States
  const [stock, setStock] = useState([]);
  const [materiels, setMateriels] = useState([]);
  const [vehicules, setVehicules] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [ambulanceFilter, setAmbulanceFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  // Modal States - Stock
  const { isOpen: isAddStockOpen, onOpen: onAddStockOpen, onClose: onAddStockClose } = useDisclosure();
  const { isOpen: isEditStockOpen, onOpen: onEditStockOpen, onClose: onEditStockClose } = useDisclosure();
  const { isOpen: isViewStockOpen, onOpen: onViewStockOpen, onClose: onViewStockClose } = useDisclosure();
  const { isOpen: isDeleteStockOpen, onOpen: onDeleteStockOpen, onClose: onDeleteStockClose } = useDisclosure();

  // Modal States - Assign/Return
  const { isOpen: isAssignOpen, onOpen: onAssignOpen, onClose: onAssignClose } = useDisclosure();
  const { isOpen: isReturnOpen, onOpen: onReturnOpen, onClose: onReturnClose } = useDisclosure();

  // Form States
  const [stockFormData, setStockFormData] = useState({ item: '', type: '', quantite: 1 });
  const [editingStock, setEditingStock] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [deletingStock, setDeletingStock] = useState(null);
  const [assignData, setAssignData] = useState({ stock_id: '', ambulance_id: '', quantite: 1 });
  const [returnData, setReturnData] = useState({ materiel_id: '', quantite: 1 });
  const [returningMateriel, setReturningMateriel] = useState(null);

  // Material Types
  const materielTypes = ['Dispositif Médical', 'Pharmacie', 'Autre'];

  // Auth Check
  useEffect(() => {
    const checkTokenAndUser = async () => {
      try {
        const check = await checkToken();
        if (!check.data.valid) {
          navigate('/auth/sign-in');
          return;
        }

        const user = await getUser();
        const userRole = user.rôle || user['rÃ´le'] || user.role;
        if (
          userRole !== 'admin' &&
          userRole !== 'régulateur' &&
          userRole !== 'rÃ©gulateur' &&
          userRole !== 'médecin regulateur' &&
          userRole !== 'medecin regulateur'
        ) {
          navigate('/auth/sign-in');
          return;
        }

        fetchData();
      } catch (error) {
        console.error("Error:", error);
        navigate('/auth/sign-in');
      }
    };
    checkTokenAndUser();
  }, [navigate]);

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoading(true);
      const [stockRes, materielsRes, vehiculesRes] = await Promise.all([
        getAllStock().catch(() => ({ data: [] })),
        getMateriels().catch(() => ({ data: [] })),
        getVehicules().catch(() => ({ data: [] })),
      ]);

      setStock(stockRes.data || []);
      setMateriels(materielsRes.data || []);
      // Filter only ambulances
      setVehicules((vehiculesRes.data || []).filter(v => v.type_vehicule?.toLowerCase() === 'ambulance'));
    } catch (err) {
      console.error("Fetch error:", err);
      toast({ title: "Erreur lors du chargement", status: "error", isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  // Get vehicle info
  const getVehicleInfo = (ambulanceId) => {
    const vehicle = vehicules.find(v => v.id === ambulanceId);
    return vehicle?.immatriculation || `ID: ${ambulanceId}`;
  };

  // ============ STATISTICS ============
  const calculatedStats = useMemo(() => {
    const stockItems = stock.length;
    const stockQuantite = stock.reduce((sum, s) => sum + (s.quantite || 0), 0);
    const materielItems = materiels.length;
    const materielQuantite = materiels.reduce((sum, m) => sum + (m.quantite || 0), 0);
    const lowStockItems = stock.filter(s => (s.quantite || 0) <= 5);
    const totalGlobal = stockQuantite + materielQuantite;

    return {
      stockCentral: { items: stockItems, quantite: stockQuantite },
      materielAmbulances: { items: materielItems, quantite: materielQuantite },
      lowStock: lowStockItems,
      totalGlobal,
    };
  }, [stock, materiels]);

  // ============ FILTERING - STOCK ============
  const filteredStock = useMemo(() => {
    return stock.filter((item) => {
      const matchSearch = searchQuery === "" ||
        (item.item?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (item.type?.toLowerCase() || '').includes(searchQuery.toLowerCase());
      const matchType = typeFilter === "all" || item.type === typeFilter;
      return matchSearch && matchType;
    });
  }, [stock, searchQuery, typeFilter]);

  // ============ FILTERING - MATERIELS ============
  const filteredMateriels = useMemo(() => {
    return materiels.filter((item) => {
      const matchSearch = searchQuery === "" ||
        (item.item?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (item.type?.toLowerCase() || '').includes(searchQuery.toLowerCase());
      const matchType = typeFilter === "all" || item.type === typeFilter;
      const matchAmbulance = ambulanceFilter === "all" || item.ambulance_id === parseInt(ambulanceFilter);
      return matchSearch && matchType && matchAmbulance;
    });
  }, [materiels, searchQuery, typeFilter, ambulanceFilter]);

  // Type counts
  const typeCounts = useMemo(() => {
    if (activeTab === 0) {
      const dispositifCount = stock.filter(s => s.type === 'Dispositif Médical').length;
      const pharmacieCount = stock.filter(s => s.type === 'Pharmacie').length;
      const autreCount = stock.filter(s => s.type === 'Autre').length;
      return {
        all: dispositifCount + pharmacieCount + autreCount,
        'Dispositif Médical': dispositifCount,
        'Pharmacie': pharmacieCount,
        'Autre': autreCount,
      };
    } else {
      const dispositifCount = materiels.filter(m => m.type === 'Dispositif Médical').length;
      const pharmacieCount = materiels.filter(m => m.type === 'Pharmacie').length;
      const autreCount = materiels.filter(m => m.type === 'Autre').length;
      return {
        all: dispositifCount + pharmacieCount + autreCount,
        'Dispositif Médical': dispositifCount,
        'Pharmacie': pharmacieCount,
        'Autre': autreCount,
      };
    }
  }, [activeTab, stock, materiels]);

  // Pagination
  const currentData = activeTab === 0 ? filteredStock : filteredMateriels;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = currentData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(currentData.length / itemsPerPage);

  // ============ STOCK HANDLERS ============
  const handleAddStock = async () => {
    if (!stockFormData.item || !stockFormData.type || !stockFormData.quantite) {
      toast({ title: 'Erreur', description: 'Veuillez remplir tous les champs', status: 'warning', duration: 3000 });
      return;
    }
    try {
      await createStock(stockFormData);
      toast({ title: 'Succès', description: 'Item ajouté au stock', status: 'success', duration: 3000 });
      onAddStockClose();
      setStockFormData({ item: '', type: '', quantite: 1 });
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.error || 'Erreur lors de l\'ajout', status: 'error', duration: 3000 });
    }
  };

  const handleEditStock = async () => {
    if (!editingStock) return;
    try {
      await updateStock(editingStock.id, stockFormData);
      toast({ title: 'Succès', description: 'Stock mis à jour', status: 'success', duration: 3000 });
      onEditStockClose();
      setEditingStock(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.error || 'Erreur lors de la mise à jour', status: 'error', duration: 3000 });
    }
  };

  const handleDeleteStock = async () => {
    if (!deletingStock) return;
    try {
      await deleteStock(deletingStock.id);
      toast({ title: 'Succès', description: 'Item supprimé du stock', status: 'success', duration: 3000 });
      onDeleteStockClose();
      setDeletingStock(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.error || 'Erreur lors de la suppression', status: 'error', duration: 3000 });
    }
  };

  const handleViewStock = (item) => {
    setViewData(item);
    onViewStockOpen();
  };

  const openEditStockModal = (item) => {
    setEditingStock(item);
    setStockFormData({ item: item.item, type: item.type, quantite: item.quantite });
    onEditStockOpen();
  };

  const openDeleteStockDialog = (item) => {
    setDeletingStock(item);
    onDeleteStockOpen();
  };

  // ============ ASSIGN HANDLER ============
  const handleAssign = async () => {
    if (!assignData.stock_id || !assignData.ambulance_id || !assignData.quantite) {
      toast({ title: 'Erreur', description: 'Veuillez remplir tous les champs', status: 'warning', duration: 3000 });
      return;
    }
    try {
      await assignStockToAmbulance(assignData);
      toast({ title: 'Succès', description: 'Matériel assigné à l\'ambulance', status: 'success', duration: 3000 });
      onAssignClose();
      setAssignData({ stock_id: '', ambulance_id: '', quantite: 1 });
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.error || 'Erreur lors de l\'assignation', status: 'error', duration: 3000 });
    }
  };

  const openAssignModal = (stockItem = null) => {
    if (stockItem) {
      setAssignData({ stock_id: stockItem.id, ambulance_id: '', quantite: 1 });
    } else {
      setAssignData({ stock_id: '', ambulance_id: '', quantite: 1 });
    }
    onAssignOpen();
  };

  // ============ RETURN HANDLER ============
  const handleReturn = async () => {
    if (!returnData.materiel_id || !returnData.quantite) {
      toast({ title: 'Erreur', description: 'Veuillez remplir tous les champs', status: 'warning', duration: 3000 });
      return;
    }
    try {
      await returnMaterielToStock(returnData);
      toast({ title: 'Succès', description: 'Matériel retourné au stock', status: 'success', duration: 3000 });
      onReturnClose();
      setReturnData({ materiel_id: '', quantite: 1 });
      setReturningMateriel(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.error || 'Erreur lors du retour', status: 'error', duration: 3000 });
    }
  };

  const openReturnModal = (materiel) => {
    setReturningMateriel(materiel);
    setReturnData({ materiel_id: materiel.id, quantite: 1 });
    onReturnOpen();
  };

  // ============ MATERIEL HANDLERS ============
  const handleIncrement = async (item) => {
    try {
      await incrementMateriel(item.id);
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.error || 'Erreur', status: 'error', duration: 3000 });
    }
  };

  const handleDecrement = async (item) => {
    try {
      await decrementMateriel(item.id);
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.error || 'Erreur', status: 'error', duration: 3000 });
    }
  };

  const handleDeleteMateriel = async (item) => {
    try {
      await deleteMateriel(item.id);
      toast({ title: 'Succès', description: 'Matériel supprimé', status: 'success', duration: 2000 });
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.error || 'Erreur', status: 'error', duration: 3000 });
    }
  };

  const topPadding = navbarHeight > 0 ? `${navbarHeight}px` : '200px';

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" px={6} pb={6} pt={0}>
        <Box maxW="9xl" mx="auto" pt={topPadding} transition="padding-top 0.3s ease">
          <LoadingSpinner message="Chargement du stock..." size="lg" fullHeight />
        </Box>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" px={6} pb={6} pt={0}>
      <Box maxW="9xl" mx="auto" pt={topPadding} transition="padding-top 0.3s ease">
        <VStack spacing={6} align="stretch">

          <Breadcrumb>
            <BreadcrumbItem>
              <BreadcrumbLink color="gray.600">Gestion des ressources</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink color="gray.900" fontWeight="medium">Gestion Stock</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Flex align="center" justify="space-between" bg="white" border="1px solid" borderColor="gray.200" borderRadius="12px" p={4}>
            <HStack spacing={3}>
              <Box
                w="40px"
                h="40px"
                bg="#006133"
                borderRadius="8px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={MdWarehouse} color="white" w="22px" h="22px" />
              </Box>
              <Heading size="md" color="gray.900">Gestion Stock</Heading>
            </HStack>
            <Text fontSize="sm" color="gray.500">Suivi du stock central et des affectations ambulances</Text>
          </Flex>

          {/* Statistics Section */}
          {showStats && (
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 4 }}
              spacing={6}
              mb={6}
            >
              <MaterialStatCard
                title="Stock Central"
                value={calculatedStats.stockCentral.items}
                subtitle={`${calculatedStats.stockCentral.quantite} unités`}
                icon={MdWarehouse}
                iconColor="#006133"
                isPositive={true}
              />
              <MaterialStatCard
                title="En Ambulances"
                value={calculatedStats.materielAmbulances.items}
                subtitle={`${calculatedStats.materielAmbulances.quantite} unités`}
                icon={MdLocalShipping}
                iconColor="#29BACD"
                isPositive={true}
              />
              <MaterialStatCard
                title="Stock Faible"
                value={calculatedStats.lowStock.length}
                subtitle="Items ≤ 5 unités"
                icon={MdWarning}
                iconColor="#F8285A"
                isPositive={calculatedStats.lowStock.length === 0}
              />
              <MaterialStatCard
                title="Total Global"
                value={calculatedStats.totalGlobal}
                subtitle="Stock + Ambulances"
                icon={MdCheckCircle}
                iconColor="#29BACD"
                isPositive={true}
              />
            </SimpleGrid>
          )}

          {/* Main Content with Tabs */}
          <Box
            bg="white"
            borderRadius="12px"
            overflow="hidden"
            boxShadow="sm"
            border="1px solid"
            borderColor="gray.200"
          >
            <Tabs variant="soft-rounded" colorScheme="blue" onChange={(index) => { setActiveTab(index); setCurrentPage(1); }}>

              <TabPanels>
                {/* ============ TAB STOCK CENTRAL ============ */}
                <TabPanel p={6}>
                  {/* Filters */}
                  <Box mb={6}>
                    <Flex gap="10px" align="center" flexWrap="nowrap" overflowX="auto">
                      <Box
                        bg="#F9F9F9"
                        border="1px solid #E5E5E5"
                        borderRadius="8px"
                        p="3px"
                        display="flex"
                        gap="5px"
                        flexWrap="nowrap"
                      >
                        <Button
                          size="sm"
                          h="30px"
                          px="10px"
                          py="2px"
                          borderRadius="8px"
                          bg={typeFilter === "all" ? "#006133" : "transparent"}
                          color={typeFilter === "all" ? "white" : "#656C7A"}
                          fontSize="13px"
                          fontWeight="500"
                          boxShadow={typeFilter === "all" ? "0px 3px 4px 0px rgba(0,0,0,0.03)" : "none"}
                          onClick={() => { setTypeFilter("all"); setCurrentPage(1); }}
                          _hover={{ bg: typeFilter === "all" ? "#005029" : "#F0F0F2", color: typeFilter === "all" ? "white" : "#455879" }}
                        >
                          Tout
                          <Badge ml="6px" bg={typeFilter === "all" ? "whiteAlpha.300" : "#E3F2FD"} color={typeFilter === "all" ? "white" : "#006133"} borderRadius="full" px="6px" fontSize="11px">
                            {typeCounts.all}
                          </Badge>
                        </Button>
                        {materielTypes.map((type) => (
                          <Button
                            key={type}
                            size="sm"
                            h="30px"
                            px="10px"
                            py="2px"
                            borderRadius="8px"
                            bg={typeFilter === type ? "#006133" : "transparent"}
                            color={typeFilter === type ? "white" : "#656C7A"}
                            fontSize="13px"
                            fontWeight="500"
                            boxShadow={typeFilter === type ? "0px 3px 4px 0px rgba(0,0,0,0.03)" : "none"}
                            onClick={() => { setTypeFilter(type); setCurrentPage(1); }}
                            _hover={{ bg: typeFilter === type ? "#005029" : "#F0F0F2", color: typeFilter === type ? "white" : "#455879" }}
                          >
                            {type}
                            <Badge ml="6px" bg={typeFilter === type ? "whiteAlpha.300" : "#E3F2FD"} color={typeFilter === type ? "white" : "#006133"} borderRadius="full" px="6px" fontSize="11px">
                              {typeCounts[type] || 0}
                            </Badge>
                          </Button>
                        ))}
                      </Box>

                      <InputGroup maxW="280px" h="40px">
                        <InputLeftElement pointerEvents="none" h="100%">
                          <Icon as={MdSearch} color="#656C7A" w="16px" h="16px" />
                        </InputLeftElement>
                        <Input
                          placeholder="Rechercher un item..."
                          value={searchQuery}
                          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                          borderRadius="8px"
                          border="1px solid"
                          borderColor="#E5E5E5"
                          bg="white"
                          fontSize="13px"
                          color="#171D28"
                          h="40px"
                          pl="40px"
                          _placeholder={{ color: "#656C7A" }}
                          _hover={{ borderColor: "#006133" }}
                          _focus={{ borderColor: "#006133", boxShadow: "0 0 0 1px #006133" }}
                        />
                      </InputGroup>

                      <HStack spacing="5px" bg="#F9F9F9" p="3px" borderRadius="8px" border="1px solid" borderColor="#E5E5E5">
                        <IconButton
                          icon={<MdGridView />}
                          aria-label="Vue grille"
                          size="sm"
                          h="30px"
                          variant="ghost"
                          bg={viewMode === "grid" ? "#006133" : "transparent"}
                          color={viewMode === "grid" ? "white" : "#656C7A"}
                          onClick={() => setViewMode("grid")}
                          borderRadius="8px"
                          _hover={{ bg: viewMode === "grid" ? "#005029" : "#F0F0F2", color: viewMode === "grid" ? "white" : "#455879" }}
                        />
                        <IconButton
                          icon={<MdList />}
                          aria-label="Vue liste"
                          size="sm"
                          h="30px"
                          variant="ghost"
                          bg={viewMode === "list" ? "#006133" : "transparent"}
                          color={viewMode === "list" ? "white" : "#656C7A"}
                          onClick={() => setViewMode("list")}
                          borderRadius="8px"
                          _hover={{ bg: viewMode === "list" ? "#005029" : "#F0F0F2", color: viewMode === "list" ? "white" : "#455879" }}
                        />
                      </HStack>

                      {hasPermission('stock_create') && (
                        <Button
                          leftIcon={<MdAdd />}
                          bg="#006133"
                          color="white"
                          onClick={onAddStockOpen}
                          borderRadius="8px"
                          h="40px"
                          px="16px"
                          fontSize="13px"
                          _hover={{ bg: "#005029" }}
                        >
                          Ajouter au stock
                        </Button>
                      )}
                    </Flex>
                  </Box>

                  {/* Stock Table/Grid - styled like Avis table (ComplexTable style) */}
                  {viewMode === "list" ? (
                    <StockTable
                      tableData={paginatedData}
                      selectedItems={selectedItems}
                      onSelect={setSelectedItems}
                      onView={(item) => handleViewStock(item)}
                      onEdit={(item) => openEditStockModal(item)}
                      onDelete={(item) => openDeleteStockDialog(item)}
                    />
                  ) : (
                    <Box>
                      {paginatedData.length === 0 ? (
                        <Box textAlign="center" py={12}>
                          <Icon as={MdInventory} boxSize="40px" opacity={0.3} mb={2} />
                          <Text color="gray.500" fontSize="lg">Aucun item dans le stock central</Text>
                        </Box>
                      ) : (
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={4}>
                          {paginatedData.map((item) => (
                            <Card
                              key={item.id}
                              bg="white"
                              borderRadius="12px"
                              border="1px solid"
                              borderColor="#E5E5E5"
                              p={4}
                              _hover={{ boxShadow: '0px 3px 4px 0px rgba(0,0,0,0.03)', transform: 'translateY(-2px)', borderColor: '#006133' }}
                              transition="all 0.2s"
                            >
                              <VStack align="stretch" spacing={3}>
                                <Flex justify="space-between" align="start">
                                  <Checkbox
                                    isChecked={selectedItems.includes(item.id)}
                                    onChange={() => {
                                      setSelectedItems(prev =>
                                        prev.includes(item.id)
                                          ? prev.filter(id => id !== item.id)
                                          : [...prev, item.id]
                                      );
                                    }}
                                  />
                                  <Menu>
                                    <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm" />
                                    <MenuList>
                                      <MenuItem icon={<MdVisibility />} onClick={() => handleViewStock(item)}>Voir</MenuItem>
                                      {hasPermission('stock_edit') && (<MenuItem icon={<MdEdit />} onClick={() => openEditStockModal(item)}>Modifier</MenuItem>)}
                                      {hasPermission('stock_delete') && (<MenuItem icon={<MdDelete />} onClick={() => openDeleteStockDialog(item)} color="red.500">Supprimer</MenuItem>)}
                                    </MenuList>
                                  </Menu>
                                </Flex>

                                <VStack spacing={1} align="center">
                                  <Icon as={MdWarehouse} w="40px" h="40px" color="#006133" />
                                  <Text fontSize="lg" fontWeight="bold" color="gray.900">{item.item}</Text>
                                  <Badge colorScheme={getTypeColor(item.type)} borderRadius="full">{item.type}</Badge>
                                </VStack>

                                <Divider />

                                <HStack justify="space-between">
                                  <Text fontSize="xs" color="gray.500">Quantité</Text>
                                  <Text fontSize="lg" fontWeight="bold" color={item.quantite <= 5 ? 'orange.500' : 'green.500'}>
                                    {item.quantite}
                                  </Text>
                                </HStack>

                                <Button
                                  size="sm"
                                  leftIcon={<MdArrowForward />}
                                  colorScheme="blue"
                                  w="100%"
                                  onClick={() => openAssignModal(item)}
                                  isDisabled={item.quantite === 0}
                                >
                                  Assigner à ambulance
                                </Button>
                              </VStack>
                            </Card>
                          ))}
                        </SimpleGrid>
                      )}
                    </Box>
                  )}

                  {/* Pagination */}
                  {paginatedData.length > 0 && (
                    <Flex justify="space-between" align="center" mt={6} pt={4} borderTop="1px solid" borderColor="gray.200">
                      <Text fontSize="sm" color="gray.600">
                        Affichage {startIndex + 1} - {Math.min(endIndex, currentData.length)} sur {currentData.length}
                      </Text>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<MdChevronLeft />}
                          aria-label="Page précédente"
                          size="sm"
                          variant="outline"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          isDisabled={currentPage === 1}
                        />
                        <Text fontSize="sm" fontWeight="medium">
                          Page {currentPage} sur {totalPages || 1}
                        </Text>
                        <IconButton
                          icon={<MdChevronRight />}
                          aria-label="Page suivante"
                          size="sm"
                          variant="outline"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          isDisabled={currentPage === totalPages || totalPages === 0}
                        />
                      </HStack>
                    </Flex>
                  )}
                </TabPanel>


              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </Box>

      {/* ============ MODAL AJOUTER AU STOCK ============ */}
      <Modal isOpen={isAddStockOpen} onClose={onAddStockClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader>
            <HStack>
              <Icon as={MdAdd} color="blue.500" />
              <Text>Ajouter au stock central</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nom de l'item</FormLabel>
                <Input
                  placeholder="Ex: Masque O2, Défibrillateur..."
                  value={stockFormData.item}
                  onChange={(e) => setStockFormData({ ...stockFormData, item: e.target.value })}
                  borderRadius="10px"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Type</FormLabel>
                <Select
                  placeholder="Sélectionner un type"
                  value={stockFormData.type}
                  onChange={(e) => setStockFormData({ ...stockFormData, type: e.target.value })}
                  borderRadius="10px"
                >
                  {materielTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Quantité</FormLabel>
                <NumberInput
                  min={1}
                  value={stockFormData.quantite}
                  onChange={(_, val) => setStockFormData({ ...stockFormData, quantite: val || 1 })}
                >
                  <NumberInputField borderRadius="10px" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAddStockClose}>Annuler</Button>
            <Button colorScheme="blue" onClick={handleAddStock}>Ajouter</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ============ MODAL MODIFIER STOCK ============ */}
      <Modal isOpen={isEditStockOpen} onClose={onEditStockClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader>
            <HStack>
              <Icon as={MdEdit} color="blue.500" />
              <Text>Modifier le stock</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nom de l'item</FormLabel>
                <Input
                  value={stockFormData.item}
                  onChange={(e) => setStockFormData({ ...stockFormData, item: e.target.value })}
                  borderRadius="10px"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Type</FormLabel>
                <Select
                  value={stockFormData.type}
                  onChange={(e) => setStockFormData({ ...stockFormData, type: e.target.value })}
                  borderRadius="10px"
                >
                  {materielTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Quantité</FormLabel>
                <NumberInput
                  min={0}
                  value={stockFormData.quantite}
                  onChange={(_, val) => setStockFormData({ ...stockFormData, quantite: val || 0 })}
                >
                  <NumberInputField borderRadius="10px" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditStockClose}>Annuler</Button>
            <Button colorScheme="blue" onClick={handleEditStock}>Enregistrer</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ============ MODAL VOIR STOCK ============ */}
      <Modal isOpen={isViewStockOpen} onClose={onViewStockClose} size="md">
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader>
            <HStack>
              <Icon as={MdVisibility} color="blue.500" />
              <Text>Détails du stock</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {viewData && (
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Text fontWeight="bold" color="gray.500">Item:</Text>
                  <Text fontWeight="medium">{viewData.item}</Text>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text fontWeight="bold" color="gray.500">Type:</Text>
                  <Badge colorScheme={getTypeColor(viewData.type)}>{viewData.type}</Badge>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text fontWeight="bold" color="gray.500">Quantité:</Text>
                  <Text fontWeight="bold" fontSize="xl" color={viewData.quantite <= 5 ? 'orange.500' : 'green.500'}>
                    {viewData.quantite}
                  </Text>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text fontWeight="bold" color="gray.500">Créé le:</Text>
                  <Text>{viewData.created_at ? new Date(viewData.created_at).toLocaleDateString('fr-FR') : '---'}</Text>
                </HStack>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onViewStockClose}>Fermer</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ============ DIALOG SUPPRIMER STOCK ============ */}
      <AlertDialog isOpen={isDeleteStockOpen} leastDestructiveRef={cancelRef} onClose={onDeleteStockClose}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Supprimer du stock
            </AlertDialogHeader>
            <AlertDialogBody>
              Êtes-vous sûr de vouloir supprimer <strong>"{deletingStock?.item}"</strong> du stock central ?
              Cette action est irréversible.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteStockClose}>Annuler</Button>
              <Button colorScheme="red" onClick={handleDeleteStock} ml={3}>Supprimer</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* ============ MODAL ASSIGNER À AMBULANCE ============ */}
      <Modal isOpen={isAssignOpen} onClose={onAssignClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader>
            <HStack>
              <Icon as={MdArrowForward} color="blue.500" />
              <Text>Assigner à une ambulance</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Item du stock</FormLabel>
                <Select
                  placeholder="Sélectionner un item"
                  value={assignData.stock_id}
                  onChange={(e) => setAssignData({ ...assignData, stock_id: e.target.value })}
                  borderRadius="10px"
                >
                  {stock.filter(s => s.quantite > 0).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.item} - {item.type} (Dispo: {item.quantite})
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Ambulance</FormLabel>
                <Select
                  placeholder="Sélectionner une ambulance"
                  value={assignData.ambulance_id}
                  onChange={(e) => setAssignData({ ...assignData, ambulance_id: e.target.value })}
                  borderRadius="10px"
                >
                  {vehicules.map((v) => (
                    <option key={v.id} value={v.id}>{v.immatriculation} {v.marque ? `- ${v.marque}` : ''}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Quantité à assigner</FormLabel>
                <NumberInput
                  min={1}
                  max={assignData.stock_id ? stock.find(s => s.id === parseInt(assignData.stock_id))?.quantite : 999}
                  value={assignData.quantite}
                  onChange={(_, val) => setAssignData({ ...assignData, quantite: val || 1 })}
                >
                  <NumberInputField borderRadius="10px" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAssignClose}>Annuler</Button>
            <Button colorScheme="blue" onClick={handleAssign}>Assigner</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ============ MODAL RETOURNER AU STOCK ============ */}
      <Modal isOpen={isReturnOpen} onClose={onReturnClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader>
            <HStack>
              <Icon as={MdArrowBack} color="orange.500" />
              <Text>Retourner au stock central</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              {returningMateriel && (
                <Box p={4} bg="gray.50" borderRadius="12px" w="100%">
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text fontWeight="bold" color="gray.500">Item:</Text>
                      <Text fontWeight="medium">{returningMateriel.item}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="bold" color="gray.500">Type:</Text>
                      <Badge colorScheme={getTypeColor(returningMateriel.type)}>{returningMateriel.type}</Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="bold" color="gray.500">Ambulance:</Text>
                      <Badge colorScheme="purple">{getVehicleInfo(returningMateriel.ambulance_id)}</Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="bold" color="gray.500">Quantité actuelle:</Text>
                      <Text fontWeight="bold">{returningMateriel.quantite}</Text>
                    </HStack>
                  </VStack>
                </Box>
              )}
              <FormControl isRequired>
                <FormLabel>Quantité à retourner</FormLabel>
                <NumberInput
                  min={1}
                  max={returningMateriel?.quantite || 1}
                  value={returnData.quantite}
                  onChange={(_, val) => setReturnData({ ...returnData, quantite: val || 1 })}
                >
                  <NumberInputField borderRadius="10px" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onReturnClose}>Annuler</Button>
            <Button colorScheme="orange" onClick={handleReturn}>Retourner au stock</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
